import React, { useCallback, useState } from "react";
import {
  RiseUserActionStudentStatusEnum,
  UpdateRiseActionStatusesMutation,
  UpdateRiseActionStatusesMutationVariables,
} from "@/__generated__/types";
import { useApolloClient, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { StatusDropdown } from "./StatusDropdown";
import { UPDATE_RISE_ACTION_STATUSES } from "@/api/mutations/updateRiseActionStatuses";
import { Student } from "@/types/Student";
import { toast } from "sonner";
import { USER_ACTION_METRICS } from "@/api/queries/userActionMetrics";

export const StatusDropdownWrapper = ({
  student,
  actionId,
}: {
  student: Student;
  actionId: string;
}) => {
  const [updatedStatus, setUpdatedStatus] =
    useState<RiseUserActionStudentStatusEnum | null>(null);
  const { t } = useTranslation("translation", {
    keyPrefix: "riseActionDetails.messages",
  });
  const client = useApolloClient();

  const riseStudentStatus =
    student.studentOrganizationStatus?.rise?.riseUserActionStudentStatus;
  const status: RiseUserActionStudentStatusEnum =
    riseStudentStatus?.status || RiseUserActionStudentStatusEnum.NotDone;
  const statusId = riseStudentStatus?.id || "";
  const name = student.studentOrganizationStatus?.name;

  const [updateStudentStatuses, { loading }] = useMutation<
    UpdateRiseActionStatusesMutation,
    UpdateRiseActionStatusesMutationVariables
  >(UPDATE_RISE_ACTION_STATUSES, {
    refetchQueries: [USER_ACTION_METRICS],
    awaitRefetchQueries: true,
    onCompleted: () => {
      toast(t("statusSuccess"), {
        description: t("statusUpdated", { name }),
        action: {
          label: t("close"),
          onClick: noop,
        },
      });

      if (riseStudentStatus) {
        client.cache.modify({
          id: client.cache.identify(riseStudentStatus),
          fields: {
            status() {
              return updatedStatus;
            },
          },
        });
      }
    },
    onError: () => {
      toast.error(t("error"), {
        description: t("statusError", { name }),
        action: {
          label: t("close"),
          onClick: noop,
        },
      });
    },
  });

  const handleUpdateStudentStatus = useCallback(
    (ids: string[], status: RiseUserActionStudentStatusEnum) => {
      setUpdatedStatus(status);
      updateStudentStatuses({
        variables: {
          riseActionId: actionId,
          ids,
          status,
        },
      });
    },
    [actionId, updateStudentStatuses],
  );

  return (
    <StatusDropdown
      value={status}
      onChange={(status) => handleUpdateStudentStatus([statusId], status)}
      loading={loading}
    />
  );
};
